/**
 * Pinia store for the Studio media library.
 * Stale-while-revalidate: hydrate from localStorage, resolve per-asset
 * CDN URLs via direct TablesDB reads on the critical path, and defer
 * the heavy `listMyMedia` function call to a background refresh.
 */

import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getPitchAsset } from "../lib/appwrite";
import {
  ensureCdnUrl,
  filterCreatorLibraryAssets,
  listMyMedia,
  type MediaAsset,
  type MediaAssetKind,
} from "../lib/media";
import {
  clearCachedMedia,
  loadCachedMedia,
  mergeMediaAssets,
  saveCachedMedia,
} from "./media-cache";
import { toUserFacingError, type UserFacingError } from "../lib/user-facing-error";

/** Match the pitches dashboard refresh cadence. */
export const MEDIA_CACHE_STALE_MS = 60_000;

export const useMediaStore = defineStore("media", () => {
  const assets = ref<MediaAsset[]>([]);
  const cdnUrls = ref<Map<string, string>>(new Map());
  const loading = ref(false);
  const refreshInFlight = ref(false);
  const refreshError = ref<UserFacingError | null>(null);
  const error = ref<UserFacingError | null>(null);
  const hasCachedData = ref(false);
  const lastRefreshedAt = ref<number | null>(null);
  const cacheUserId = ref<string | null>(null);

  let refreshPromise: Promise<void> | null = null;

  const assetsList = computed(() => assets.value);

  function isStale(): boolean {
    return (
      lastRefreshedAt.value == null ||
      Date.now() - lastRefreshedAt.value > MEDIA_CACHE_STALE_MS
    );
  }

  function persistCache(): void {
    if (!cacheUserId.value) return;
    saveCachedMedia(cacheUserId.value, assets.value, cdnUrls.value);
    lastRefreshedAt.value = Date.now();
  }

  function ingestAssets(rows: MediaAsset[], merge: boolean): void {
    const visible = filterCreatorLibraryAssets(rows);
    assets.value = merge ? mergeMediaAssets(assets.value, visible) : visible;
    const next = new Map(cdnUrls.value);
    for (const row of visible) {
      if (row.$id && row.cdnUrl) {
        next.set(row.$id, ensureCdnUrl(row.cdnUrl));
      }
    }
    cdnUrls.value = next;
  }

  function hydrateFromCache(creatorUserId: string): boolean {
    cacheUserId.value = creatorUserId;
    const cached = loadCachedMedia(creatorUserId);
    if (!cached || cached.assets.length === 0) {
      hasCachedData.value = false;
      return false;
    }
    assets.value = cached.assets;
    cdnUrls.value = cached.cdnUrls;
    hasCachedData.value = true;
    loading.value = false;
    if (cached.savedAt) {
      const parsed = Date.parse(cached.savedAt);
      if (Number.isFinite(parsed)) lastRefreshedAt.value = parsed;
    }
    return true;
  }

  /**
   * Critical-path resolver for section image previews. Uses the
   * in-memory / localStorage cache first, then direct `getPitchAsset`
   * per id — no `listMyMedia` / function cold start on this path.
   */
  async function resolveCdnUrls(assetIds: string[]): Promise<Record<string, string>> {
    const out: Record<string, string> = {};
    const missing: string[] = [];
    for (const id of assetIds) {
      if (!id) continue;
      const cached = cdnUrls.value.get(id);
      if (cached) {
        out[id] = cached;
        continue;
      }
      const row = assets.value.find((a) => a.$id === id);
      if (row?.cdnUrl) {
        const url = ensureCdnUrl(row.cdnUrl);
        out[id] = url;
        cdnUrls.value = new Map(cdnUrls.value).set(id, url);
        continue;
      }
      missing.push(id);
    }
    if (missing.length === 0) return out;

    const lookups = await Promise.allSettled(
      missing.map((id) => getPitchAsset(id)),
    );
    const next = new Map(cdnUrls.value);
    for (let i = 0; i < missing.length; i++) {
      const settled = lookups[i]!;
      if (settled.status !== "fulfilled") continue;
      const row = settled.value;
      if (!row?.$id || !row.cdnUrl) continue;
      const url = ensureCdnUrl(row.cdnUrl);
      out[row.$id] = url;
      next.set(row.$id, url);
    }
    cdnUrls.value = next;
    if (cacheUserId.value) persistCache();
    return out;
  }

  async function refreshLibrary(
    creatorUserId: string,
    opts: {
      kind?: MediaAssetKind | null;
      limit?: number;
      offset?: number;
      force?: boolean;
      background?: boolean;
    } = {},
  ): Promise<void> {
    if (!creatorUserId) return;
    if (!opts.force && hasCachedData.value && !isStale()) return;
    if (refreshPromise) return refreshPromise;

    cacheUserId.value = creatorUserId;
    const shouldMerge = hasCachedData.value;
    if (!shouldMerge && !opts.background) {
      loading.value = true;
    }
    error.value = null;
    refreshError.value = null;
    refreshInFlight.value = true;

    refreshPromise = (async () => {
      try {
        const res = await listMyMedia({
          kind: opts.kind ?? null,
          limit: opts.limit ?? 100,
          offset: opts.offset ?? 0,
        });
        ingestAssets(res.assets ?? [], shouldMerge);
        persistCache();
        hasCachedData.value = true;
      } catch (err: unknown) {
        const facing = toUserFacingError(err);
        if (hasCachedData.value) {
          refreshError.value = facing;
        } else {
          error.value = facing;
        }
      } finally {
        refreshInFlight.value = false;
        loading.value = false;
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  /** Fire-and-forget library warm — safe on pitch-edit mount. */
  function warmLibraryBackground(
    creatorUserId: string,
    opts: { kind?: MediaAssetKind | null; limit?: number } = {},
  ): void {
    if (!creatorUserId) return;
    void refreshLibrary(creatorUserId, { ...opts, background: true });
  }

  function onAssetRemoved(assetId: string): void {
    assets.value = assets.value.filter((a) => a.$id !== assetId);
    const next = new Map(cdnUrls.value);
    next.delete(assetId);
    cdnUrls.value = next;
    persistCache();
  }

  function onAssetUpserted(asset: MediaAsset): void {
    ingestAssets([asset], true);
    persistCache();
  }

  function clearCache(): void {
    clearCachedMedia(cacheUserId.value);
    cacheUserId.value = null;
    assets.value = [];
    cdnUrls.value = new Map();
    loading.value = false;
    refreshInFlight.value = false;
    refreshError.value = null;
    error.value = null;
    hasCachedData.value = false;
    lastRefreshedAt.value = null;
    refreshPromise = null;
  }

  return {
    assets,
    assetsList,
    cdnUrls,
    loading,
    refreshInFlight,
    refreshError,
    error,
    hasCachedData,
    lastRefreshedAt,
    hydrateFromCache,
    resolveCdnUrls,
    refreshLibrary,
    warmLibraryBackground,
    onAssetRemoved,
    onAssetUpserted,
    clearCache,
    isStale,
  };
});
